import { useMemo } from 'react'
import Link from 'next/link'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { InvestIcon } from '@/atoms/Icons/InvestIcon'
import { LightIcon } from '@/atoms/Icons/LightIcon'
import { PayItForwardIcon } from '@/atoms/Icons/PayItForwardIcon'
import { UsersThreeIcon } from '@/atoms/Icons/UsersThreeIcon'
import { LabelMD, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { AngelFundingLink } from '@/molecules/InvestLink'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export function useExplainerContent() {
  const { t } = useTranslate('about')
  return useMemo(
    () => [
      {
        background: 'bg-black',
        content: (
          <Translate t={t} i18nKey={'defineLightBlurbV3'}>
            <ParagraphSM>
              Whatever is true, honest, noble, just, authentic, lovely, admirable, or excellent.
            </ParagraphSM>
            <ParagraphSM>
              Angel Studios stands as a testament to the power of collective creativity. Our mission to amplify light
              lets stories have a place to shine. Stories that have raised, inspired, and moved us deserve a place to be
              shared and cherished.
            </ParagraphSM>
            <ParagraphSM>
              Excellent and authentic filmmaking addresses the true needs of fans that are often unheard. Animated TV
              series like{' '}
              <Link className="underline" href={paths.shows.wingfeatherSaga}>
                The Wingfeather Saga
              </Link>{' '}
              and{' '}
              <Link className="underline" href={paths.shows.tuttleTwins}>
                Tuttle Twins
              </Link>{' '}
              have engaged kids worldwide to think beyond their world for a whirlwind of adventure.
            </ParagraphSM>
          </Translate>
        ),
        icon: <LightIcon className="shrink-0" size={48} />,
        image: '/v1703151031/angel-studios/about/c11d0549e4d02094b7dbbddf1d016258.webp',
        subtitle: t('howDoWeDefineLight', 'How do we define light?'),
        title: t('amplifyingLight', 'Amplifying Light'),
      },
      {
        background: 'bg-core-gray-950',
        button: (
          <LinkButton className="h-14 w-full md:w-1/3 xl:w-1/2" href={paths.payItForward.index} variant="white">
            <LabelMD>{t('payItForwardv2', 'Pay it Forward')}</LabelMD>
          </LinkButton>
        ),
        content: (
          <Translate t={t} i18nKey="payItForwardBlurb">
            <ParagraphSM>
              Angel Studios’ shows are free for viewers around the world, and we intend to keep it that way. But it
              takes millions of dollars and a community to produce high-quality content. That’s where you come in.
            </ParagraphSM>
            <ParagraphSM>
              At Angel Studios, we firmly believe in the power of community and the principle of Pay it Forward. Our
              commitment to providing free access to high-quality shows for viewers worldwide is at the heart of our
              mission. However, achieving this goal requires substantial financial resources and the active involvement
              of a supportive community. The production of top-notch content demands significant investments, ranging in
              the millions of dollars. It is the collective effort of our viewers, contributors, and supporters that
              enables us to continue creating content that captivates and inspires. You play a vital role in sustaining
              our commitment to free and accessible entertainment for audiences globally.
            </ParagraphSM>
          </Translate>
        ),
        icon: <PayItForwardIcon className="shrink-0" color="white" size={48} />,
        image: '/v1703151060/angel-studios/about/c08fd1c58397fc3bb6f8fb35dbd98dc2.webp',
        reverse: true,
        title: t('payItForwardv2', 'Pay it Forward'),
      },
      {
        background: 'bg-black',
        button: (
          <LinkButton className="h-14 w-full md:w-1/3 xl:w-1/2" href={paths.guild.join} variant="white">
            <LabelMD>{t('joinTheGuild', 'Join the Guild')}</LabelMD>
          </LinkButton>
        ),
        content: (
          <Translate t={t} i18nKey="theAngelGuildBlurb">
            <ParagraphSM>
              Hollywood&apos;s predictable plot lines or Angel Studios&apos; crowd-approved hits? You decide. We trust
              that you hold the key to what truly resonates as great entertainment. Angel Guild members receive special
              perks including 2 free movie tickets for every theatrical release, early access, exclusive livestreams,
              and merchandise discounts.
            </ParagraphSM>
            <ParagraphSM>
              Your membership in the Angel Guild is a testament to your commitment to quality, community-driven
              entertainment, and it opens the door to an immersive experience that goes beyond traditional viewing.
            </ParagraphSM>
          </Translate>
        ),
        icon: <GuildIconMono className="shrink-0" color="white" size={48} />,
        image: 'v1706832342/angel-studios/guild/image_frame.webp',
        title: t('theAngelGuild', 'The Angel Guild'),
      },
      {
        button: (
          <Button className="h-14 w-full md:w-1/3 xl:w-1/2" variant="white">
            <AngelFundingLink href={paths.external.invest.index} linkContext={'about'}>
              <LabelMD>{t('learnMore', 'Learn More')}</LabelMD>
            </AngelFundingLink>
          </Button>
        ),
        content: (
          <Translate t={t} i18nKey="crowdFundingBlurb">
            <ParagraphSM>
              Through Angel Studios’ platform, thousands of Angel investors choose which titles will be created, funded,
              and distributed. Angel Studios allows creators and audiences to form passionate communities around their
              creative projects, making the story behind the story as important as the final project itself.
            </ParagraphSM>
            <ParagraphSM>
              Angel Studios takes on a new model as a company that amplifies light. Rather than taking the traditional
              approach of other studios, we focus on crowdfunding projects to bring projects to life that viewers want
              to watch. We see the trends around streaming and media turn into “cutting the cord” with cable and moving
              to streaming, and see an opportunity to share creative projects with passionate communities.
            </ParagraphSM>
          </Translate>
        ),
        icon: <UsersThreeIcon className="shrink-0" color="white" size={48} />,
        image: '/v1706128612/angel-studios/about/bf3019fc368e9bf0d9fc25ae7fdb064c.webp',
        reverse: true,
        title: t('crowdfunding', 'Crowdfunding'),
      },
      {
        background: 'bg-black',
        button: (
          <Button className="h-14 w-full md:w-1/3 xl:w-1/2" variant="white">
            <AngelFundingLink href={paths.external.invest.index} linkContext={'about'}>
              <LabelMD>{t('learnMore', 'Learn More')}</LabelMD>
            </AngelFundingLink>
          </Button>
        ),
        content: (
          <Translate t={t} i18nKey="investingBlurb">
            <ParagraphSM>
              If you aren’t sure about joining the Guild, there are other ways to help get promising projects off the
              ground.
            </ParagraphSM>
            <ParagraphSM>
              Angel Funding is the division that handles the crowdfunding portal and helps filmmakers in the sale of
              securities. Anyone interested in investing can look at the various projects that are open for investments
              and invest in the projects that pique your attention. There are several projects currently funding, and
              your investment could be the final push to get it over the finish line.
            </ParagraphSM>
          </Translate>
        ),
        icon: <InvestIcon className="shrink-0" color="white" size={48} />,
        image: '/v1704437242/angel-studios/about/10d20e4ac1bc37f0a23d55ec57d71a77.webp',
        title: t('investingInAngelStudiosProjects', 'Investing in Angel Studios Projects'),
      },
    ],
    [t],
  )
}
